import { Box, Button, Input } from '@robocorp/components';
import { IconTrash, IconUploadCloud } from '@robocorp/icons/iconic';
import {
  ChangeEvent,
  FC,
  Fragment,
  InputHTMLAttributes,
  MouseEvent,
  useRef,
  useState,
} from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ListContent, ListHeading } from './old-ds-components';

type OwnProps = {
  buttonText: string;
  header?: string;
  loading?: boolean;
  uploadPercentage?: number;
  editing?: boolean;
  uploadButtonVariant?: 'primary' | 'secondary';
  creatingActivityTemplate?: boolean;
  setFileToUpload: (file?: File) => void;
};

type Props = OwnProps &
  WithTranslation & {
    inputProps: Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { error?: string | boolean };
  };

const FileUploader: FC<Props> = ({
  t,
  header,
  loading,
  buttonText,
  uploadPercentage,
  editing,
  uploadButtonVariant,
  creatingActivityTemplate,
  setFileToUpload,
  inputProps,
}) => {
  const [file, setFile] = useState<File>();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = inputProps?.id ?? 'contained-button-file';
  const fileChangeResetter = file?.name;

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
      setFileToUpload(event.target.files[0]);
    }
  };

  const onUploadClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const clearFile = () => {
    setFile(undefined);
    setFileToUpload(undefined);
  };

  const renderFileInfo = () => {
    if (file) {
      return (
        <Fragment>
          <Box display="flex" flexWrap="wrap">
            <ListContent type="text" secondaryTop={t('name')}>
              {file?.name}
            </ListContent>
            <ListContent type="text" secondaryTop={t('size')}>
              {`${(file.size / 1024 / 1024).toFixed(5)} MB`}
            </ListContent>
            {creatingActivityTemplate && (
              <Box ml="auto">
                <Button
                  variant="ghost"
                  type="button"
                  icon={IconTrash}
                  onClick={clearFile}
                  aria-label="delete"
                />
              </Box>
            )}
          </Box>
        </Fragment>
      );
    }

    return <Fragment />;
  };

  return (
    <Fragment>
      {header && <ListHeading>{header}</ListHeading>}
      {/* TODO: Replace with DS FileUploader component when done https://linear.app/issue/DS-72/fileuploader */}
      <Box
        alignSelf="center"
        ml={editing ? 0 : 'auto'}
        pr={!creatingActivityTemplate && editing ? 0 : 20}
      >
        <label htmlFor={inputId} style={{ cursor: 'pointer' }}>
          {(!creatingActivityTemplate || (creatingActivityTemplate && !file)) && (
            <Button
              icon={IconUploadCloud}
              variant={uploadButtonVariant || editing ? 'secondary' : 'primary'}
              loading={loading}
              onClick={onUploadClick}
            >
              {buttonText}
              {loading && ` (${uploadPercentage}%)`}
            </Button>
          )}
        </label>
        <Input
          ref={inputRef}
          key={fileChangeResetter}
          id={inputId}
          type="file"
          onChange={onChange}
          style={{ display: 'none' }}
          aria-label="file"
          {...inputProps}
        />
      </Box>
      {file && (
        <Box width={[1]} pt={!creatingActivityTemplate ? 12 : 0}>
          <Box
            width={!creatingActivityTemplate ? [1, 1, 1, 3 / 4] : 1}
            pl={editing || creatingActivityTemplate ? 0 : 20}
          >
            {renderFileInfo()}
          </Box>
        </Box>
      )}
    </Fragment>
  );
};

FileUploader.defaultProps = {
  uploadPercentage: 0,
  header: '',
};

export default withTranslation()(FileUploader);
